import React, { useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import { motion, AnimatePresence } from "framer-motion";

const SLIDE_DELAY = 5000;

const SLIDES = [
  {
    image: "https://res.cloudinary.com/ducc0wskb/image/upload/v1778277632/bg-sa_tym8yw.webp",
    label: "Handmade Cards",
    headline: "Made by hand. Given with purpose.",
    subtext: "70% of every purchase goes to people serving in Ukraine.",
    cta: { text: "Shop the collection", to: "/supportUkraine" },
  },
  {
    image: "https://res.cloudinary.com/ducc0wskb/image/upload/v1771546967/blogs/ncmf0p9zmd8efpnxufc7.webp",
    label: "From the Blog",
    headline: "Writing about the things that matter.",
    subtext: "Faith, movement, food, technology — honest words on everyday life.",
    cta: { text: "Read the blog", to: "/blogs" },
  },
  {
    image: "https://res.cloudinary.com/ducc0wskb/image/upload/v1771364810/blogs/jsyqmipkrypwvvz7eqrm.webp",
    label: "Photography",
    headline: "Paying attention to the world.",
    subtext: "Photo stories from New York and beyond.",
    cta: { text: "View stories", to: "/stories" },
  },
];

const HeroSlider = () => {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [ready, setReady] = useState(false);

  const handleSlideChange = useCallback((swiper) => {
    setActiveIndex(swiper.realIndex);
  }, []);

  if (!ready) {
    setTimeout(() => setReady(true), 0);
  }

  const slide = SLIDES[activeIndex];

  return (
    <div className="relative w-full h-[56vw] min-h-[320px] md:h-screen overflow-hidden bg-black">
      {/* Invisible full-area link covering the whole slide */}
      {slide.cta?.to ? (
        <Link to={slide.cta.to} className="absolute inset-0 z-[5]" aria-label={slide.cta.text} />
      ) : slide.cta?.href ? (
        <a href={slide.cta.href} className="absolute inset-0 z-[5]" aria-label={slide.cta.text} />
      ) : null}

      {/* ── Swiper ── */}
      <Swiper
        modules={[Autoplay, EffectFade]}
        effect="fade"
        loop={SLIDES.length > 1}
        speed={1400}
        allowTouchMove={true}
        autoplay={{
          delay: SLIDE_DELAY,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        onSlideChange={handleSlideChange}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        className="absolute inset-0 w-full h-full"
      >
        {SLIDES.map((s, i) => {
          const isFirst = i === 0;
          return (
            <SwiperSlide key={i} className="relative w-full h-full">
              <img
                src={s.image}
                alt={s.headline}
                className="w-full h-full object-cover object-center"
                loading={isFirst ? "eager" : "lazy"}
                fetchPriority={isFirst ? "high" : "low"}
                decoding={isFirst ? "sync" : "async"}
                width="1920"
                height="1080"
              />
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* ── Gradient overlay ── */}
      <div
        className="absolute inset-0 pointer-events-none z-[1]"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 45%, rgba(0,0,0,0.08) 75%, transparent 100%)",
        }}
      />

      {/* ── Caption ── */}
      <div className="absolute bottom-6 md:bottom-16 left-4 md:left-10 z-[4] w-[calc(100%-2rem)] md:w-[55%] pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="space-y-2"
          >
            {/* Label */}
            {slide.label && (
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <span className="inline-block text-[10px] md:text-xs font-sourcesans font-medium uppercase tracking-[0.18em] text-white/80 bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20">
                  {slide.label}
                </span>
              </motion.div>
            )}

            {/* Headline */}
            <motion.h2
              className="text-base sm:text-xl md:text-3xl lg:text-4xl font-medium font-sourcesans text-white leading-snug drop-shadow-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
            >
              {slide.headline}
            </motion.h2>

            {/* CTA — text only, whole slide is the link */}
            {slide.cta && (
              <motion.p
                className="hidden md:block text-xs font-sourcesans text-white/50 tracking-widest uppercase pt-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45, duration: 0.6 }}
              >
                {slide.cta.text} →
              </motion.p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Slide counter ── */}
      <div className="absolute top-4 right-4 z-[4] pointer-events-none hidden md:flex items-center gap-2">
        <span className="font-mono text-[0.65rem] tracking-[0.2em] text-white/40 tabular-nums">
          {String(activeIndex + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
        </span>
      </div>

      {/* ── Progress bar ── */}
      <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/10 z-20 overflow-hidden pointer-events-none">
        <motion.div
          key={activeIndex}
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: (SLIDE_DELAY + 1200) / 1000, ease: "linear" }}
          className="h-full bg-white/50"
        />
      </div>

    </div>
  );
};

export default HeroSlider;